import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';

import { useCan } from '@/components/can/Can';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useAttributes } from '@/hooks/useAttributes';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { PremiumStores, STORE_STATUS } from '@/services/premiumStores/premiumStores.types';
import { useLocation } from '@tanstack/react-router';
import { useDeletePremiumStoreInfo } from '../../hooks/useDeletePremiumStoreInfo';
import { usePremiumStoreMutation } from '../../hooks/usePremiumStoreMutation';
import { usePremiumStoreResetPassword } from '../../hooks/usePremiumStoreResetPassword';
import { ApprovedStoreTableActionBar } from './ActionBar';
import { getColumns } from './Column';

export function ApprovedStoreTable({
  data,
  isLoading,
  pagination,
}: {
  data: PremiumStores[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const { mutateAsync } = usePremiumStoreMutation();
  const { onOpen } = useDialogFormStore();

  const { mutateAsync: resetPremiumStorePassword } = usePremiumStoreResetPassword();
  const { mutate: deletePremiumStoreInfo } = useDeletePremiumStoreInfo();
  const { t, locale } = useDynamicLocale();
  const { openConfirmation } = useConfirmationStore();
  const location = useLocation();

  const { data: locationData } = useAttributes('location');
  const canEditLine = useCan({ allOf: [PERMS.club.premium.line.manage] });
  const canEditSubDomain = useCan({ allOf: [PERMS.club.premium.subdomain.manage] });

  const columns = React.useMemo(
    () =>
      getColumns(
        mutateAsync,
        onOpen,
        resetPremiumStorePassword,
        deletePremiumStoreInfo,
        t,
        openConfirmation,
        locationData ?? [],
        locale,
        location.href,
        canEditLine,
        canEditSubDomain,
      ),
    [
      mutateAsync,
      onOpen,
      resetPremiumStorePassword,
      t,
      openConfirmation,
      deletePremiumStoreInfo,
      locationData,
      locale,
      location.href,
      canEditLine,
      canEditSubDomain,
    ],
  );

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { table, statusFilter, onStatusFilterChange, locationFilter, onLocationFilterChange } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'id', desc: true }],
    },
    meta: {
      title: t('storeManagement.premiumStores.table.title.approvedStores'),
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
    statusFilterKey: 'status',
  });

  return (
    <>
      <DataTable table={table} className="rounded-t-none">
        <DataTableToolbar
          statusFilter={statusFilter as STORE_STATUS}
          onStatusFilterChange={onStatusFilterChange}
          locationFilter={locationFilter as string}
          onLocationFilterChange={onLocationFilterChange}
        />
        <ApprovedStoreTableActionBar />
      </DataTable>
    </>
  );
}
